import React, { useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  ListItemText,
  Menu,
  MenuItem,
  makeStyles,
} from '@material-ui/core';
import { SmallClose } from '~/components/icons/SmallClose';

const useStyles = makeStyles(() => ({
  selectButton: {
    display: 'flex',
    minHeight: 'var(--utilities-size-2-500, 40px)',
    padding:
      'var(--utilities-size-0-500, 8px) var(--utilities-size-0-750, 12px)',
    alignItems: 'center',
    columnGap: 'var(--utilities-size-1-250, 20px)',
    rowGap: 'var(--utilities-size-0-750, 12px)',
    flexWrap: 'wrap',
    gap: 'var(--utilities-size-1-250, 20px)',
    alignSelf: 'stretch',
    borderRadius: 'var(--utilities-border-radius-default, 8px)',
    border: '1px solid var(--border-control-default, rgba(23, 28, 38, 0.20))',
    boxShadow: '0px 1px 2px 0px rgba(23, 28, 38, 0.05)',
    color: 'var(--content-primary, #2C313A)',
    fontFamily: 'var(--typography-font-family-default, Inter)',
    fontSize: 'var(--typography-font-size-md, 16px)',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: 'var(--typography-line-height-md-line, 24px)',
    letterSpacing: 'var(--typography-letter-spacing-default, 0px)',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  allProperties: {
    display: 'flex',
    height: '32px',
    minHeight: 'var(--utilities-size-2-000, 32px)',
    padding:
      'var(--utilities-size-0-375, 6px) var(--utilities-size-0-500, 8px)',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-500, 8px)',
    margin: '-4px -8px',
    borderRadius: 'var(--utilities-border-radius-md, 4px)',
    background: 'var(--surface-tint-weak, rgba(23, 28, 38, 0.05))',
    boxShadow: '0px 1px 2px 0px rgba(23, 28, 38, 0.05)',
    color: 'var(--content-primary, #2C313A)',
    fontFamily: 'var(--typography-font-family-default, Inter)',
    fontSize: 'var(--typography-font-size-md, 16px)',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: 'var(--typography-line-height-md-line, 24px)',
    letterSpacing: 'var(--typography-letter-spacing-default, 0px)',
    '&:hover': {
      background:
        'var(--surface-hover-tint-weak-hover, rgba(23, 28, 38, 0.10))',
    },
  },
  propertyItem: {
    color: 'var(--content-primary, #2C313A)',
    textAlign: 'center',
    fontFamily: 'var(--typography-font-family-default, Inter)',
    fontSize: 'var(--typography-font-size-sm, 14px)',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: 'var(--typography-line-height-sm-line, 20px)',
    letterSpacing: 'var(--typography-letter-spacing-default, 0px)',
  },
  menuContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 'var(--utilities-size-1-000, 16px)',
    backgroundImage: 'url(/public/static/icons/seenic/chevron-down.svg)',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '20px 20px',
    backgroundPosition: 'right 12px center',
    '&.MuiMenu-paper': {
      maxHeight: '200px',
    },
  },
  searchInput: {
    width: '100%',
    height: '40px',
    display: 'flex',
    minHeight: 'var(--utilities-size-2-500, 40px)',
    padding: '0 0 10px 28px',
    gap: 'var(--utilities-size-0-500, 8px)',
    borderBottom:
      '1px solid var(--border-control-default, rgba(23, 28, 38, 0.20))',
    background: 'var(--surface-default, #FFF)',
    alignItems: 'center',
    alignSelf: 'stretch',
    boxSizing: 'border-box',
    backgroundImage: 'url(/public/static/icons/seenic/search.svg)',
    backgroundRepeat: 'no-repeat',
    backgroundPosition:
      'var(--utilities-size-0-500, 8px) var(--utilities-size-0-500, 8px)',
    backgroundSize: 'var(--utilities-size-1-000)',
    color: 'var(--content-primary)',
    '&:hover': {
      backgroundImage: 'url(/public/static/icons/seenic/search.svg)',
      backgroundRepeat: 'no-repeat',
      backgroundPosition:
        'var(--utilities-size-0-500, 8px) var(--utilities-size-0-500, 8px)',
      backgroundSize: 'var(--utilities-size-1-000)',
    },
    '&:focus, &:focus-visible': {
      backgroundImage: 'url(/public/static/icons/seenic/search.svg)',
      backgroundRepeat: 'no-repeat',
      backgroundPosition:
        'var(--utilities-size-0-500, 8px) var(--utilities-size-0-500, 8px)',
      backgroundSize: 'var(--utilities-size-1-000)',
      outline: 'unset',
    },
    '&:-internal-autofill-selected': {
      backgroundImage: 'url(/public/static/icons/seenic/search.svg)',
      backgroundRepeat: 'no-repeat',
      backgroundPosition:
        'var(--utilities-size-0-500, 8px) var(--utilities-size-0-500, 8px)',
      backgroundSize: 'var(--utilities-size-1-000)',
    },
  },
  menu: {
    width: '100%',
    height: '330px',
    inset: '54px 0px 0px 0px !important',
  },
  menuItem: {
    display: 'flex',
    padding:
      'var(--utilities-size-0-500, 8px) var(--utilities-size-0-750, 12px)',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-500, 8px)',
    alignSelf: 'stretch',
    background: 'var(--surface-default, #FFF)',
    '&:hover': {
      background: 'var(--surface-hover-default-hover, #F8F9FC)',
      '& .MuiTypography-root': {
        color: 'var(--content-accent, #5E59F3)',
      },
    },
  },
  checkbox: {
    display: 'flex',
    width: '20px',
    height: '20px',
    padding: 'var(--utilities-size-0-125, 2px)',
    alignItems: 'center',
    borderRadius: 'var(--utilities-border-radius-md, 4px)',
    border: '1px solid var(--border-control-default, rgba(23, 28, 38, 0.20))',
    background: 'var(--surface-default, #FFF)',
    boxShadow: '0px 1px 2px 0px rgba(23, 28, 38, 0.05)',
    '& .MuiSvgIcon-root': {
      visibility: 'hidden',
    },
    '&.Mui-checked': {
      display: 'flex',
      padding: 'var(--utilities-size-0-125, 2px)',
      alignItems: 'center',
      borderRadius: 'var(--utilities-border-radius-md, 4px)',
      background: 'var(--surface-accent, #5E59F3)',
      boxShadow: '0px 1px 2px 0px rgba(23, 28, 38, 0.05)',
      backgroundImage: 'url(/static/icons/seenic/check.svg)',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'var(--utilities-size-1-000)',
      '&:hover': {
        background: 'var(--surface-accent, #5E59F3)',
        boxShadow: '0px 1px 2px 0px rgba(23, 28, 38, 0.05)',
        backgroundImage: 'url(/static/icons/seenic/check.svg)',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'var(--utilities-size-1-000)',
      },
      '& .MuiSvgIcon-root': {
        visibility: 'hidden',
      },
    },
  },
  checkboxAllSelected: {
    '&.Mui-checked': {
      backgroundImage: 'url(/static/icons/seenic/all-check.svg)',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'var(--utilities-size-1-000)',
    },
  },
  menuItemText: {
    '& .MuiTypography-root': {
      color: 'var(--content-primary, #2C313A)',
      fontFamily: 'var(--typography-font-family-default, Inter)',
      fontSize: 'var(--typography-font-size-sm, 14px)',
      fontStyle: 'normal',
      fontWeight: 500,
      lineHeight: 'var(--typography-line-height-sm-line, 20px)',
      letterSpacing: 'var(--typography-letter-spacing-default, 0px)',
    },
  },
  searchText: {
    fontWeight: 700,
  },
  mutedText: {
    '& .MuiTypography-root': {
      color: 'var(--content-primary-muted)',
    },
  },
}));

const CustomPropertySelectWithCheckboxes = ({
  options,
  selectedValues,
  setSelectedValues,
  authUser,
  label,
  emptyList,
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [searchText, setSearchText] = useState('');

  const isSuperAdmin = authUser.role === 'superadmin';

  const authUserCompaniesAccessList = new Set(
    authUser.propertiesAccessList.map(({ name }) => name),
  );

  const filteredOptions = useMemo(() => {
    return options.filter(option =>
      option.toLowerCase().includes(searchText.toLowerCase()),
    );
  }, [options, searchText]);

  const isAllSelected = isSuperAdmin
    ? selectedValues.length === options.length
    : selectedValues.length === filteredOptions.length;

  const handleOpen = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchText('');
  };

  const handleToggleOption = option => {
    if (option === 'All') {
      if (isAllSelected) {
        const ownCompaniesAccessList = selectedValues.filter(
          n => !authUserCompaniesAccessList.has(n),
        );
        setSelectedValues(isSuperAdmin ? [] : ownCompaniesAccessList);
      } else {
        setSelectedValues(options);
      }
    } else {
      if (selectedValues.includes(option)) {
        setSelectedValues(selectedValues.filter(value => value !== option));
      } else {
        setSelectedValues([...selectedValues, option]);
      }
    }
  };

  const isOptionDisabled = option =>
    !authUser.propertiesAccessList.some(company => company.name === option);

  const highlightText = (text, highlight) => {
    if (!highlight.trim()) {
      return text;
    }
    const regex = new RegExp(`(${highlight})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      part.toLowerCase() === highlight.toLowerCase() ? (
        <span key={i} className={classes.searchText}>
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  return (
    <Box className={classes.menuContainer}>
      <Box className={classes.selectButton} onClick={handleOpen}>
        {isAllSelected ? (
          <Box className={classes.allProperties}>All properties</Box>
        ) : selectedValues.length > 0 ? (
          selectedValues.map((item, index) => (
            <Box
              key={index}
              className={classes.allProperties}
              onMouseEnter={() => setHoveredItem(item)}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <Box className={classes.propertyItem}>{item}</Box>
              {authUser.role === 'superadmin' && (
                <SmallClose
                  color={hoveredItem === item ? '2C313A' : '171C26'}
                  onClick={e => {
                    e.stopPropagation();
                    handleToggleOption(item);
                  }}
                />
              )}
              {authUser.role === 'admin' && !isOptionDisabled(item) && (
                <SmallClose
                  color={hoveredItem === item ? '2C313A' : '171C26'}
                  onClick={e => {
                    e.stopPropagation();
                    handleToggleOption(item);
                  }}
                />
              )}
            </Box>
          ))
        ) : (
          `Choose ${label}...`
        )}
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        className={classes.menu}
        MenuListProps={{
          style: { width: '100%' },
        }}
      >
        <input
          name="text"
          className={classes.searchInput}
          value={searchText}
          placeholder="Search"
          onKeyDown={e => e.stopPropagation()}
          onKeyUp={e => e.stopPropagation()}
          onChange={e => {
            setSearchText(e.target.value);
          }}
        />
        <MenuItem
          disableRipple
          className={classes.menuItem}
          onClick={() => handleToggleOption('All')}
          disabled={authUser.role === 'admin' && emptyList}
        >
          <Checkbox
            disableRipple
            className={classes.checkbox}
            checked={isAllSelected}
          />
          <ListItemText
            className={classes.menuItemText}
            primary={`All properties`}
          />
        </MenuItem>
        {filteredOptions.map((option, index) => (
          <MenuItem
            disableRipple
            className={classes.menuItem}
            key={`${option}-${index}`}
            onClick={() => handleToggleOption(option)}
            disabled={authUser.role === 'admin' && isOptionDisabled(option)}
          >
            <Checkbox
              disableRipple
              className={`${classes.checkbox} ${
                isAllSelected ? classes.checkboxAllSelected : ''
              }`}
              checked={selectedValues.includes(option)}
            />
            <ListItemText
              className={`${classes.menuItemText} ${
                isAllSelected ? classes.mutedText : ''
              }`}
              primary={<>{highlightText(option, searchText)}</>}
            />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default CustomPropertySelectWithCheckboxes;
